import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils.js';

const badgeVariants = cva(
  [
    'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
    'transition-colors',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'border-transparent bg-[var(--color-primary)]/20 text-[var(--color-primary)]',
          'shadow-[0_0_8px_rgba(59,130,246,0.15)]',
        ].join(' '),
        secondary: [
          'border-[var(--color-border)] bg-[var(--color-secondary)] text-[var(--color-secondary-foreground)]',
        ].join(' '),
        destructive: [
          'border-transparent bg-[var(--color-destructive)]/20 text-[var(--color-destructive)]',
          'shadow-[0_0_8px_rgba(220,38,38,0.15)]',
        ].join(' '),
        outline: [
          'border-[var(--color-border)] text-[var(--color-foreground)]',
        ].join(' '),
        success: [
          'border-transparent bg-[var(--color-success)]/20 text-[var(--color-success)]',
          'shadow-[0_0_8px_rgba(34,197,94,0.15)]',
        ].join(' '),
        // Log level specific variants with glow effects
        debug: [
          'border-transparent bg-[var(--color-debug)]/15 text-[var(--color-debug)]',
          'shadow-[0_0_6px_rgba(139,92,246,0.2)]',
        ].join(' '),
        info: [
          'border-transparent bg-[var(--color-info)]/15 text-[var(--color-info)]',
          'shadow-[0_0_6px_rgba(59,130,246,0.2)]',
        ].join(' '),
        warn: [
          'border-transparent bg-[var(--color-warn)]/15 text-[var(--color-warn)]',
          'shadow-[0_0_6px_rgba(245,158,11,0.2)]',
        ].join(' '),
        error: [
          'border-transparent bg-[var(--color-error)]/15 text-[var(--color-error)]',
          'shadow-[0_0_6px_rgba(239,68,68,0.2)]',
        ].join(' '),
        fatal: [
          'border-transparent bg-[var(--color-fatal)]/20 text-[var(--color-fatal)]',
          'shadow-[0_0_8px_rgba(220,38,38,0.3)]',
          'font-semibold',
        ].join(' '),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
